if (!customElements.get('player-products-tabs')) {
  class PlayerProductsTabs extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.abortController = null;
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('popstate', this.handlePopState);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('popstate', this.handlePopState);
    }

    getTabs() {
      return Array.from(this.querySelectorAll('[data-player-products-tab]'));
    }

    handleClick(event) {
      const tab = event.target.closest('[data-player-products-tab]');
      if (!tab) return;

      event.preventDefault();
      if (tab.getAttribute('aria-selected') === 'true') return;

      this.switchTab(tab.href, tab);
    }

    handleKeydown(event) {
      const tabs = this.getTabs();
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let targetIndex = null;
      switch (event.key) {
        case 'ArrowRight':
          targetIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          targetIndex = 0;
          break;
        case 'End':
          targetIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      tabs[targetIndex].focus();
    }

    handlePopState() {
      const tabs = this.getTabs();
      const activeTab = tabs.find((tab) => tab.href === window.location.href) || tabs[0];
      if (!activeTab) return;

      this.switchTab(window.location.href, activeTab, { fromPopState: true });
    }

    async switchTab(targetUrl, tabEl, { fromPopState = false } = {}) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      const panel = this.closest('.player-products')?.querySelector('.player-products__panel');
      const currentGrid = panel?.querySelector('collection-grid');

      try {
        const html = await fetch(targetUrl, { signal }).then((response) => response.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.updateTabsState(doc);
        this.updateGrid(doc, currentGrid);

        if (!fromPopState) window.history.pushState({}, '', targetUrl);
        tabEl?.focus();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Player products tab update error:', error);
        window.location.href = targetUrl;
      } finally {
        if (!signal.aborted) this.abortController = null;
      }
    }

    updateTabsState(doc) {
      const newTabs = Array.from(doc.querySelectorAll('[data-player-products-tab]'));
      const currentTabs = this.getTabs();

      currentTabs.forEach((tab, index) => {
        const newTab = newTabs[index];
        if (!newTab) return;
        tab.setAttribute('aria-selected', newTab.getAttribute('aria-selected'));
        tab.setAttribute('tabindex', newTab.getAttribute('tabindex'));
        tab.href = newTab.href;
      });
    }

    updateGrid(doc, currentGrid) {
      const newGrid = doc.querySelector('collection-grid');
      if (!newGrid || !currentGrid) return;
      currentGrid.replaceWith(newGrid);
    }
  }

  customElements.define('player-products-tabs', PlayerProductsTabs);
}
